//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStepsData } from './stepsData';
import RightSection from './RightSection';
import LeftSection from './LeftSection';
import { useConfig } from '../../Config';
import { useUserAuth } from '../../UserAuth';
import LoopDataService from '../../utils/LoopDataService';
import { COLOR_OPTIONS } from './DefineLoop/DefineLoop';
import {
  CreateLoopResponse,
  FetchKeycloakUserResponse,
  Teamspace,
  TeamspaceTaskResponse,
  TeamspaceProvisionData,
  PollTeamspaceTask,
  LoopRetryResponse,
  LoopTaskResponse,
} from '../Sidebar/SidebarTypes';
import { fetchWrapper } from '../../FetchWrapper';
import { useTeamspaceContext } from '../../TeamspaceContext';
import TenantDataService from '../../utils/TenantDataService';
import { useTranslation } from 'react-i18next';
import { failedLoop, useLoopContext } from '../../LoopContext';

type LoopStatus = 'success' | 'fail' | 'waiting';
interface LandingProps {
  mode: 'loop' | 'teamspace';
}
const MAX_LOOP_NAME_LENGTH = 20;
const MAX_DESCRIPTION_LENGTH = 120;

export interface RawUser {
  id: string;
  email: string;
  username: string;
  fullname: string;
  firstname: string;
  lastname: string;
  role: string;
}

interface NonMember {
  email: string;
}

/**
 *
 * @param mode It specifies whether to follow the Loop creation flow or Teamspace creation flow
 * @returns
 */
function Landing({ mode }: Readonly<LandingProps>) {
  const { services } = useConfig();
  const { feature, user, isAuthenticated } = useUserAuth();
  const [step, setStep] = useState(1);
  const navigate = useNavigate();
  const [loopName, setLoopName] = useState('');
  const [description, setDescription] = useState('');
  const [progress, setProgress] = useState<number>(0);
  const [isSuccess, setIsSuccess] = useState<LoopStatus>('waiting');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [fetchedUsers, setFetchedUsers] = useState<RawUser[]>([]);
  const [hasFetchedUsers, setHasFetchedUsers] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<RawUser[]>([]);
  const {
    teamspaceId,
    tenantId,
    _setTeamspacesUpdated,
    createNewTeamSpace,
    teamspaceName,
    incompleteTeamspaceTask,
  } = useTeamspaceContext();
  const [selectedColor, setSelectedColor] = useState(COLOR_OPTIONS[0].color);
  const [defineError, setDefineError] = useState('');
  const [errMsgSuccPage, setErrMsgSuccPage] = useState('');
  const [teamspaceID, setTeamspaceID] = useState('');
  const loopDataService = new LoopDataService(
    () => {
      return user;
    },
    () => {
      return isAuthenticated;
    },
  );
  const tenantDataService = new TenantDataService(
    () => {
      return user;
    },
    () => {
      return isAuthenticated;
    },
  );
  const { t } = useTranslation();
  const [nonMemberUsers, setNonMemberUsers] = useState<NonMember[]>([]);
  const [teamspaceCreationFailed, setTeamspaceCreationFailed] = useState(false);
  const [teamspaceRegistrationFailed, setTeamspaceRegistrationFailed] =
    useState(false);
  const [createdLoopId, setCreatedLoopId] = useState<string | null>(null);
  const [loopCreationFailed, setLoopCreationFailed] = useState(false);
  const [loopRegistrationFailed, setLoopRegistrationFailed] =
    useState<failedLoop | null>(null);
  const [updateTeamspaceTask, setUpdateTeamspaceTask] =
    useState<PollTeamspaceTask>({ selfLink: '', type: '', noTaskFound: false });
  const [failedTask, setFailedTask] = useState('');
  const [incompleteTask, setIncompleteTask] = useState('');
  const [taskProgressIndex, setTaskProgressIndex] = useState(0);
  const [taskProgressVersion, setTaskProgressVersion] = useState(0);
  const [currentDescriptionLoop, setCurrentDescriptionLoop] = React.useState<
    string[]
  >([]);
  const {
    retryLoop,
    setRetryLoop,
    incompleteLoops,
    setIncompleteLoop,
    setIncompleteLoops,
    failedLoops,
    setFailedLoops,
  } = useLoopContext();
  const [warning, setWarning] = useState('');
  const [pendingResume, setPendingResume] = useState(false);
  const [executeLoopTasks, setExecuteLoopTasks] = useState(false);
  const [demoDataType, setdemoDataType] = useState('');
  const [importDemoData, setImportDemoData] = useState(false);

  // Only set state for resume, don't call executeLoopTaskSequence here
  useEffect(() => {
    if (retryLoop !== null && retryLoop.id !== null) {
      const selectedloop = incompleteLoops.find(
        (loop) => loop.id === retryLoop.id,
      );
      if (loopRegistrationFailed) {
        setCreatedLoopId(loopRegistrationFailed?.id ?? null);
        setPendingResume(true);
      } else if (selectedloop) {
        const deepClonedLoop = JSON.parse(JSON.stringify(selectedloop));
        setIncompleteLoop(selectedloop);
        setFailedTask(deepClonedLoop.failedTask);
        setTeamspaceID(deepClonedLoop.teamSpaceId);
        setCreatedLoopId(deepClonedLoop.id);
        setLoopName(deepClonedLoop.displayName);
        setSelectedMembers(deepClonedLoop.associatedUserIds);
        setDescription(deepClonedLoop.description);
        setdemoDataType(deepClonedLoop.demoDataType || '');
        setImportDemoData(!!deepClonedLoop.demoDataType);
        setStep(3);
        setPendingResume(true);
      } else {
        console.warn('No matching loop found for retryLoop.id:', retryLoop.id);
      }
    }
  }, [retryLoop, incompleteLoops]);

  // Only call handleRetryLoopCreationBanner when all state is set and pendingResume is true
  useEffect(() => {
    if (pendingResume && createdLoopId && (teamspaceID || teamspaceId)) {
      handleRetryLoopCreationBanner(createdLoopId, teamspaceID ?? teamspaceId);
      setPendingResume(false);
    }
  }, [pendingResume, createdLoopId, teamspaceID, teamspaceId]);

  const handleNextStep = async () => {
    setIsSubmitted(true);
    if (
      loopName.length === 0 ||
      loopName.length > MAX_LOOP_NAME_LENGTH ||
      description.length > MAX_DESCRIPTION_LENGTH
    ) {
      return;
    }

    if (step === 1 && mode === 'teamspace') {
      const isTaken = await tenantDataService.checkIfTeamSpaceNameIsUnique(
        tenantId,
        loopName,
      );
      if (!isTaken) {
        setDefineError(t('teamspace-exists'));
        return;
      } else {
        setDefineError('');
      }
    }
    // If user is at step 1 of the flow => fetch the members
    if (step === 1 && !hasFetchedUsers) {
      setStep((prevStep) => prevStep + 1);
      try {
        const data: FetchKeycloakUserResponse = await fetchKeycloakUsers();
        setFetchedUsers(data);
        setHasFetchedUsers(true);
      } catch (error) {
        console.error('Failed to fetch users:', error);
      }
    } else if (step < steps.length) {
      setStep((prevStep) => prevStep + 1);
    }
  };

  const {
    steps,
    images,
    pages,
    welcomeMessages,
    descriptions,
    headings,
    taskDescriptions,
  } = useStepsData({
    mode,
    fetchedUsers,
    loopName,
    setLoopName,
    setProgress,
    description,
    setDescription,
    isSuccess,
    isSubmitted,
    setIsSubmitted,
    setSelectedMembers,
    teamspaceId: teamspaceID,
    defineError,
    errMsgSuccPage,
    setErrMsgSuccPage,
    setNonMemberUsers,
    createdLoopId: createdLoopId || '',
    updateTeamspaceTask,
    setTeamspaceCreationFailed,
    failedTask,
    incompleteTask,
    handleNextStep,
    taskProgressVersion,
    taskProgressIndex,
    setTaskProgressIndex,
    currentDescriptionLoop,
    setCurrentDescriptionLoop,
    setLoopCreationFailed,
    warning,
    setWarning,
    executeLoopTasks,
    selectedColor,
    setSelectedColor,
    demoDataType,
    setdemoDataType,
    importDemoData,
    setImportDemoData,
  });

  const [showSuccess, setShowSuccess] = useState(false);

  const percentage = Math.min(Math.max(progress, 0), 100);
  const [creationStepLocked, setCreationStepLocked] = useState(false);

  useEffect(() => {
    setCreationStepLocked(false);
  }, [retryLoop, incompleteLoops, mode]);

  useEffect(() => {
    // Fetch teamspace to check if teamspace has a failed task
    const checkTeamspaceFailedTask = async () => {
      if (teamspaceId) {
        try {
          const teamspaceResp =
            await tenantDataService.getTeamspace(teamspaceId);
          if (teamspaceResp?.taskstatus === 'FAILED') {
            // If a task failed during teamspace creation then return user to creation step
            setTeamspaceID(teamspaceId);
            setStep(3);
            setFailedTask(teamspaceResp?._links?.failedTask?.href);
          }
        } catch (error) {
          console.error('Error fetching teamspace:', error);
        }
      }
    };

    const checkTeamspaceIncompleteTask = async () => {
      if (incompleteTeamspaceTask && teamspaceId) {
        setTeamspaceID(teamspaceId);
        setStep(3);
        setIncompleteTask(incompleteTeamspaceTask);
      }
    };
    if (!createNewTeamSpace) {
      checkTeamspaceFailedTask();
      checkTeamspaceIncompleteTask();
    }
  }, [teamspaceId, incompleteTeamspaceTask]);

  useEffect(() => {
    if (percentage === 100) {
      setShowSuccess(true);
    } else {
      setShowSuccess(false);
    }
  }, [percentage]);

  useEffect(() => {
    if (showSuccess && step === steps.length - 1) {
      const timer = setTimeout(() => setStep(steps.length), 800);
      return () => clearTimeout(timer);
    }
  }, [showSuccess, step, steps.length]);

  useEffect(() => {
    const timer = setTimeout(
      () => (!incompleteTeamspaceTask && !retryLoop ? setProgress(10) : null),
      4000,
    ); // 10% after 1s
    return () => clearTimeout(timer);
  }, []);
  const handleHome = () => {
    navigate('/');
  };

  useEffect(() => {
    const inviteUsers = async () => {
      if (nonMemberUsers.length > 0 && teamspaceID) {
        const emailList = nonMemberUsers
          .map((user) => user.email)
          .filter((email) => email);

        try {
          await tenantDataService.inviteUsers(teamspaceID, emailList);
        } catch (error) {
          console.error('Error inviting users:', error);
        }
      }
    };

    inviteUsers();
  }, [nonMemberUsers, teamspaceID]);

  useEffect(() => {
    if (createNewTeamSpace) {
      setIsSubmitted(false);
      setLoopName('');
      setStep(1);
    }
  }, [createNewTeamSpace]);

  const handleBackStep = () => {
    if (step >= steps.length - 1) return;
    setStep((prevStep) => prevStep - 1);
  };

  async function fetchKeycloakUsers(): Promise<RawUser[]> {
    if (mode === 'teamspace') {
      try {
        const responseBody: FetchKeycloakUserResponse =
          await tenantDataService.fetchKeycloakUsers();

        const rawUsers: RawUser[] = Array.isArray(responseBody)
          ? responseBody
          : [];
        return rawUsers;
      } catch (error) {
        if (error instanceof Error) {
          throw new Error(
            `Error in fetching the users from Keycloak: ${error.message}`,
          );
        } else {
          throw new Error(
            'Failed in fetching users from Keycloak: An Unknown error occurred',
          );
        }
      }
    } else {
      try {
        const url =
          services?.tenant?.url + '/teamspaces/' + teamspaceId + '/users';
        const accessToken = user.access_token;
        const responseBody = await fetchWrapper(url, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        const rawUsers: RawUser[] = Array.isArray(responseBody)
          ? responseBody
          : [];
        return rawUsers;
      } catch (error) {
        if (error instanceof Error) {
          throw new Error(
            `Error in fetching the users of a Teamspace: ${error.message}`,
          );
        } else {
          throw new Error(
            'Error in fetching the users from Teamspace: An Unknown error occurred',
          );
        }
      }
    }
  }

  const createLoop = async (loopData: Partial<CreateLoopResponse>) => {
    try {
      const response = await loopDataService.createLoop(loopData);
      return response;
    } catch (error) {
      const err =
        error instanceof Error
          ? new Error(`${error.message}`)
          : new Error('An unknown error occurred');
      err.name = 'LoopCreationError';
      throw err;
    }
  };

  const handleCreateLoop = async () => {
    setIsSubmitted(true);
    if (
      loopName.length === 0 ||
      loopName.length > MAX_LOOP_NAME_LENGTH ||
      description.length > MAX_DESCRIPTION_LENGTH
    )
      return;

    setStep((prevStep) => (prevStep < steps.length ? prevStep + 1 : prevStep));

    if (mode === 'loop') {
      try {
        const loopData: Partial<CreateLoopResponse> = {
          tenantId: tenantId,
          teamSpaceId: teamspaceId,
          displayName: loopName,
          description: description,
          workflowType: 'agile',
          associatedUserIds: [
            user.profile.preferred_username ?? '',
            ...selectedMembers.map((m) => m.username),
          ],
          ...(importDemoData && demoDataType ? { demoDataType } : {}),
        };
        const response = await createLoop(loopData);
        setCreatedLoopId(response?.id);
        setTeamspaceID(teamspaceId ?? '');
        setLoopCreationFailed(false);
        setLoopRegistrationFailed(null);
        setTimeout(() => {
          setIsSuccess('success');
        }, 2000);
      } catch (error) {
        setLoopCreationFailed(true);
        setTeamspaceCreationFailed(false);
        const newLoop = {
          id: '-1', // Setting id to -1 because initial loop registration failed
          name: loopName,
          mode: 'loop',
          teamSpaceName: teamspaceName,
        }; // This new loop we should set as loopRegistrationFailed value so that we know which loop failed to be registered
        setLoopRegistrationFailed(newLoop);
        const errorMessage =
          error instanceof Error
            ? t('error-create-loop', {
                detail: error.message?.slice(0, 150) ?? '',
              })
            : t('error-create-loop-unknown');
        setErrMsgSuccPage(errorMessage);
        console.error('Failed to create loop:', error);
        setTimeout(() => {
          setIsSuccess('fail');
        }, 2000);
      }
    } else {
      try {
        const provisionData: TeamspaceProvisionData = {
          name: loopName,
          tenantId: tenantId ?? '',
          description,
          usernames: selectedMembers.map((m) => m.username),
        };
        const response: Teamspace =
          await tenantDataService.provisionTeamspace(provisionData);
        setTeamspaceID(response?.id);
        if (response?.id) {
          await tenantDataService.updateTeamspaceIconColor(
            response.id,
            selectedColor,
          );
        }
        setTeamspaceRegistrationFailed(false);
        setTeamspaceCreationFailed(false);
        setLoopCreationFailed(false);
        setTimeout(() => {
          setIsSuccess('success');
        }, 2000);
      } catch (error) {
        setTeamspaceRegistrationFailed(true);
        setTeamspaceCreationFailed(true);
        setLoopCreationFailed(false);
        const errorMessage =
          error instanceof Error
            ? t('error-create-teamspace', {
                detail: error.message?.slice(0, 150) ?? '',
              })
            : t('error-create-teamspace-unknown');
        setErrMsgSuccPage(errorMessage);
        console.error(
          '[Teamspace Creation] Failed to create Teamspace:',
          error,
        );
        setTimeout(() => {
          setIsSuccess('fail');
        }, 2000);
      }
    }
  };

  function filterOutCreation(
    tasks: { displayName: string; type: string; description: string }[],
    taskStatuses?: ('success' | 'fail' | 'waiting')[],
  ) {
    // If taskStatuses is provided, set CREATION to 'success' when filtering
    return tasks.filter((task, idx) => {
      if (task.type === 'CREATION' && taskStatuses) {
        taskStatuses.push('success');
        return false;
      }
      return task.type !== 'CREATION';
    });
  }

  const executeLoopTaskSequence = async (
    loopId: string,
    teamspaceID: string,
    skipCreation = false,
    resumeFailedTaskType: string | null = null,
  ) => {
    setExecuteLoopTasks(true);
    try {
      const response = await loopDataService.loopTaskStatus(
        teamspaceID,
        feature ?? '',
        demoDataType || '',
      );

      // Build unified ordered sequence from backend categories generically
      const categories = Array.isArray(response?.categories)
        ? [...response.categories]
        : [];
      categories.sort(
        (categoryA, categoryB) =>
          (categoryA.order ?? 0) - (categoryB.order ?? 0),
      );

      const fullSequence: {
        displayName: string;
        type: string;
        description: string;
      }[] = [];
      categories.forEach((cat) => {
        const tasks = Array.isArray(cat.tasks) ? [...cat.tasks] : [];
        tasks.sort((taskA, taskB) => (taskA.order ?? 0) - (taskB.order ?? 0));
        tasks.forEach((task) => {
          fullSequence.push({
            displayName: task.displayName,
            type: task.type,
            description: task.description,
          });
        });
      });

      // Guarantee CREATION first (add or move)
      const creationIdx = fullSequence.findIndex(
        (t) => t.type?.toUpperCase() === 'CREATION',
      );
      if (creationIdx === -1) {
        fullSequence.unshift({
          displayName: 'CREATION',
          type: 'CREATION',
          description: 'Creation',
        });
      } else if (creationIdx > 0) {
        const creationTask = fullSequence.splice(creationIdx, 1)[0];
        fullSequence.unshift(creationTask);
      }

      // Execution sequence optionally skips creation
      const executionSequence = skipCreation
        ? fullSequence.filter((t, idx) => idx !== 0) // remove first which is CREATION
        : fullSequence;

      const taskStatuses: ('success' | 'fail' | 'waiting')[] = [];
      if (resumeFailedTaskType) {
        // Mark CREATION as success implicitly if resuming
        taskStatuses[0] = 'success';
        setCurrentDescriptionLoop(taskStatuses);
        setCreationStepLocked(true);
      }

      // Iterate over each task in the execution sequence
      for (
        let taskIndex = 0;
        taskIndex < executionSequence.length;
        taskIndex++
      ) {
        setIsSuccess('waiting');
        // Progress index uses original sequence positions (CREATION is index 0 if present)
        setTaskProgressIndex(taskIndex + 1);
        setTaskProgressVersion((version) => version + 1);

        const taskType = executionSequence[taskIndex].type.toUpperCase();

        // CREATION shouldn't appear in executionSequence when skipCreation; if present just mark success
        if (taskType === 'CREATION') {
          setIsSuccess('success');
          setProgress(((taskIndex + 1) / executionSequence.length) * 100);
          continue;
        }
        const taskResp = await loopDataService.loopTaskCreation(
          teamspaceID,
          loopId,
          taskType,
          true,
        );

        if (
          !taskResp ||
          Object.keys(taskResp).length === 0 ||
          !taskResp._links
        ) {
          setProgress(((taskIndex + 1) / executionSequence.length) * 100);
          setIsSuccess('success');
          setTaskProgressIndex(taskIndex + 1);
          setTaskProgressVersion((version) => version + 1);

          await new Promise((res) => setTimeout(res, 50));
          continue;
        }

        const selfLink = taskResp._links.self.href;

        let status = 'WAITING';
        let attempts = 0;
        while (
          status !== 'COMPLETED' &&
          status !== 'FAILED' &&
          attempts < 200
        ) {
          const resp = await tenantDataService.invokeHREF(selfLink);
          status = resp.status;
          if (status === 'COMPLETED') break;
          if (status === 'FAILED') break;
          await new Promise((res) => setTimeout(res, 6000));
          attempts++;
        }

        if (status === 'FAILED') {
          setIsSuccess('fail');
          setErrMsgSuccPage('Failed to complete all tasks. Please try again.');
          // Update status for UI
          if (resumeFailedTaskType) {
            const updatedStatuses = [...taskStatuses];
            // Always keep CREATION as 'success' if locked
            if (creationStepLocked) {
              updatedStatuses[0] = 'success';
            }
            updatedStatuses[taskIndex] = 'fail';
            setCurrentDescriptionLoop(updatedStatuses);
          }
          return;
        } else if (status === 'COMPLETED') {
          setIsSuccess('success');
        }

        setProgress(((taskIndex + 1) / executionSequence.length) * 100);
        // Update status for UI
        if (resumeFailedTaskType) {
          const updatedStatuses = [...taskStatuses];
          // Always keep CREATION as 'success' if locked
          if (creationStepLocked) {
            updatedStatuses[0] = 'success';
          }
          updatedStatuses[taskIndex] = 'success';
          setCurrentDescriptionLoop(updatedStatuses);
        }
        await new Promise((res) => setTimeout(res, 50)); // Wait for render so UI reflects task status
        if (taskIndex + 1 === executionSequence.length) {
          // End of sequence so we are done executing the loop tasks
          setExecuteLoopTasks(false);
        }
      }
      setIsSuccess('success');
    } catch (error) {
      const contains = failedLoops.some((loop) => loop.id === loopId);
      if (!contains) {
        const newLoop = {
          id: loopId,
          name: loopName,
          mode: 'loop',
          teamSpaceName: teamspaceName,
        };
        setFailedLoops([...failedLoops, newLoop]); // Add new loop to end
      }
      setIsSuccess('fail');
      setErrMsgSuccPage('Failed to complete all tasks. Please try again.');
      setExecuteLoopTasks(false);
    }
  };

  const handleRetryTeamspaceCreation = async () => {
    if (teamspaceRegistrationFailed) {
      // If initial teamspace registration fails then we need to retry entire creation flow
      setErrMsgSuccPage('');
      setIsSuccess('waiting');
      handleCreateLoop();
    } else {
      try {
        // Retry endpoint is async; the SuccessMsg component will poll
        // provisioning-status for the updated task statuses.
        await tenantDataService.retryFailedTeamspaceTask(teamspaceID);
        setErrMsgSuccPage('');
        setProgress(10);
        // `isSuccess` may already be 'success' from the initial creation, in
        // which case setting it to 'success' again is a no-op and the
        // SuccessMessage useEffect never re-fires. Bumping the monotonic
        // `taskProgressVersion` counter forces the effect to re-run and
        // restart polling without relying on setTimeout for React batching.
        setIsSuccess('success');
        setTaskProgressVersion((version) => version + 1);
      } catch (error) {
        console.error('Error in retrying teamspace task:', error);
        setErrMsgSuccPage(
          'Failed to retry teamspace task. Please try again later.',
        );
      }
    }
  };

  const handleRetryTeamspaceCreationLater = () => {
    _setTeamspacesUpdated(true);
    navigate('/');
  };

  const handleRetryLoopCreation = async () => {
    setErrMsgSuccPage('');

    if (loopRegistrationFailed) {
      setRetryLoop(loopRegistrationFailed);
    } else {
      const incomplete = {
        id: createdLoopId,
        name: loopName,
        mode: 'loop',
        teamSpaceName: teamspaceName,
        registrationFailed: true,
      };
      const loopToRetry = await loopDataService.fetchALoop(
        teamspaceId,
        createdLoopId,
      );
      const failedLoops = [...incompleteLoops, loopToRetry];
      setIncompleteLoops(failedLoops);
      setRetryLoop(incomplete);
    }
  };

  async function handleRetryLoopCreationBanner(
    createdLoopId: string | null,
    teamspaceID: string,
  ) {
    try {
      if (loopRegistrationFailed) {
        setIsSuccess('waiting');
        handleCreateLoop();
      } else {
        // Retry endpoint is async; the SuccessMsg component will poll loop status with tasks.
        await loopDataService.retryFailedLoopTask(teamspaceID, createdLoopId);
        setErrMsgSuccPage('');
        setProgress(10);
        setExecuteLoopTasks(false);
        setRetryLoop(null);
        // `isSuccess` may already be 'success' from the initial creation, in
        // which case setting it to 'success' again is a no-op and the
        // SuccessMessage useEffect never re-fires. Bumping the monotonic
        // `taskProgressVersion` counter forces the effect to re-run and
        // restart polling via createTasks → pollLoopProvisioningStatus
        // without relying on setTimeout for React batching.
        setIsSuccess('success');
        setTaskProgressVersion((version) => version + 1);
      }
    } catch (error) {
      setRetryLoop(null);
      console.error('Error in retrying loop task:', error);
      setErrMsgSuccPage('Failed to retry loop task. Please try again later.');
    }
  }

  const handleRetryLoopCreationLater = () => {
    navigate('/');
  };

  const pageTitle =
    mode === 'teamspace' ? t('create-teamspace') : t('Create-Loop');

  return (
    <div id="container" data-testid="container" className="flex flex-col">
      {/* Content Area */}
      <div
        id="subcontainer"
        data-testid="subcontainer"
        className="flex-1 overflow-auto bg-[#F4F4F4] p-4 dark:bg-carbon-darkBg"
      >
        <div
          id="id-create-landing-content-panel"
          data-testid="create-landing-content-panel"
          className="rounded-lg bg-white p-4 dark:bg-carbon-darkPanel"
        >
          <div
            data-testid="create-landing-grid"
            className="grid grid-cols-2 items-start gap-6"
          >
            {/* Left Column */}
            <LeftSection
              mode={mode}
              step={step}
              steps={steps}
              images={images}
              welcomeMessages={welcomeMessages}
              descriptions={descriptions}
              progress={progress}
              showSuccess={showSuccess}
              teamspaceCreationFailed={teamspaceCreationFailed}
              loopCreationFailed={loopCreationFailed}
              taskProgressIndex={taskProgressIndex}
              taskDescriptions={taskDescriptions}
              currentDescriptionLoop={currentDescriptionLoop}
              setCurrentDescriptionLoop={setCurrentDescriptionLoop}
            />
            {/* Right Column — bordered panel */}
            <div
              id="id-create-landing-right-panel"
              data-testid="create-landing-right-panel"
              className="flex h-full flex-col rounded border border-[#e0e0e0] bg-white p-6 dark:border-carbon-darkBorderSurface dark:bg-carbon-darkSurface"
            >
              <RightSection
                mode={mode}
                loopName={loopName}
                step={step}
                steps={steps}
                images={images}
                pages={pages}
                headings={headings}
                progress={progress}
                handleNextStep={handleNextStep}
                handleBackStep={handleBackStep}
                handleCreate={handleCreateLoop}
                handleHome={handleHome}
                handleRetryTeamspace={handleRetryTeamspaceCreation}
                handleRetryLaterTeamspace={handleRetryTeamspaceCreationLater}
                showSuccess={showSuccess}
                errMsgSuccPage={errMsgSuccPage}
                teamspaceId={teamspaceID}
                handleRetryLoop={handleRetryLoopCreation}
                handleRetryLaterLoop={handleRetryLoopCreationLater}
                warning={warning}
                defineError={defineError}
                selectedColor={selectedColor}
                selectedMembers={selectedMembers}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Landing;
